import { useMemo, useState } from 'react';
import { AddSpecialtyControl } from './AddSpecialtyControl';
import { BulkCopyDialog } from './BulkCopyDialog';
import { RemoveSpecialtyDialog } from './RemoveSpecialtyDialog';
import { SpecialtyCoverageRow } from './SpecialtyCoverageRow';
import type {
  ServiceRange,
  SpecialtyCoverageEditorProps,
  SpecialtyCoverageItem,
} from './types';

export function SpecialtyCoverageEditor({
  value,
  onChange,
  specialtyOptions,
  permissions,
  specialtyOptionsLoading = false,
  fieldErrors = {},
  disabled = false,
}: SpecialtyCoverageEditorProps) {
  const [removeTargetId, setRemoveTargetId] = useState<string | null>(null);
  const [copySourceId, setCopySourceId] = useState<string | null>(null);

  const nameById = useMemo(() => {
    const map = new Map(specialtyOptions.map((opt) => [opt.id, opt.name]));
    return map;
  }, [specialtyOptions]);

  const updateItem = (
    specialtyId: string,
    updater: (item: SpecialtyCoverageItem) => SpecialtyCoverageItem,
  ) => {
    onChange(value.map((item) => (item.specialtyId === specialtyId ? updater(item) : item)));
  };

  const toggleMode = (specialtyId: string, mode: ServiceRange) => {
    updateItem(specialtyId, (item) => {
      const hasMode = item.serviceRanges.includes(mode);
      if (hasMode && item.serviceRanges.length === 1) return item;
      return {
        ...item,
        serviceRanges: hasMode
          ? item.serviceRanges.filter((value) => value !== mode)
          : [...item.serviceRanges, mode],
      };
    });
  };

  const handleRemoveRequest = (item: SpecialtyCoverageItem) => {
    if (item.isPersisted) {
      setRemoveTargetId(item.specialtyId);
      return;
    }
    onChange(value.filter((row) => row.specialtyId !== item.specialtyId));
  };

  const copySource = value.find((item) => item.specialtyId === copySourceId) ?? null;
  const removeTarget = value.find((item) => item.specialtyId === removeTargetId) ?? null;

  return (
    <div className="space-y-4">
      <div>
        <h3 className="font-recoleta-medium text-2xl font-normal text-text-primary">
          Specialties & Coverage
        </h3>
        <p className="mt-1 text-sm text-text-secondary">
          Set coverage and delivery modes for each specialty.
          {permissions.canManageAttachments
            ? ' Add or remove specialties here; categories update automatically.'
            : ' Specialty list is managed by an admin.'}
        </p>
      </div>

      <div className="divide-y divide-border-subtle">
        {value.map((item) => (
          <SpecialtyCoverageRow
            key={item.specialtyId}
            item={item}
            name={nameById.get(item.specialtyId) ?? item.specialtyId}
            canManageAttachments={permissions.canManageAttachments}
            fieldError={fieldErrors[item.specialtyId]}
            disabled={disabled}
            onCoverageChange={(scope, states) =>
              updateItem(item.specialtyId, (row) => ({
                ...row,
                coverageScope: scope,
                coverageStates: states,
              }))
            }
            onToggleMode={(mode) => toggleMode(item.specialtyId, mode)}
            onRequestRemove={() => handleRemoveRequest(item)}
            onRequestBulkCopy={() => setCopySourceId(item.specialtyId)}
          />
        ))}
        {value.length === 0 ? (
          <p className="rounded-lg border border-dashed border-border-subtle bg-card-subtle px-4 py-8 text-center text-sm text-text-secondary">
            No specialties yet.
            {permissions.canManageAttachments ? ' Add one to configure coverage.' : ''}
          </p>
        ) : null}
      </div>

      {permissions.canManageAttachments ? (
        <AddSpecialtyControl
          options={specialtyOptions}
          selectedIds={value.map((item) => item.specialtyId)}
          loading={specialtyOptionsLoading}
          disabled={disabled}
          onAdd={(specialtyId) => {
            // No inherit, no default (issue 15/24) — the admin must pick scope
            // and delivery mode explicitly before this specialty is saveable.
            onChange([
              ...value,
              {
                specialtyId,
                coverageScope: null,
                serviceRanges: [],
                coverageStates: [],
                isPersisted: false,
              },
            ]);
          }}
        />
      ) : null}

      {copySource ? (
        <BulkCopyDialog
          open={Boolean(copySourceId)}
          onOpenChange={(open) => {
            if (!open) setCopySourceId(null);
          }}
          source={copySource}
          allItems={value}
          specialtyOptions={specialtyOptions}
          onConfirm={(targetIds) => {
            const targetSet = new Set(targetIds);
            onChange(
              value.map((item) =>
                targetSet.has(item.specialtyId)
                  ? {
                      ...item,
                      coverageScope: copySource.coverageScope,
                      serviceRanges: [...copySource.serviceRanges],
                      coverageStates: copySource.coverageStates.map((state) => ({
                        ...state,
                        countyIds: [...state.countyIds],
                      })),
                    }
                  : item,
              ),
            );
          }}
        />
      ) : null}

      {removeTarget ? (
        <RemoveSpecialtyDialog
          open={Boolean(removeTargetId)}
          specialtyName={nameById.get(removeTarget.specialtyId) ?? removeTarget.specialtyId}
          onOpenChange={(open) => {
            if (!open) setRemoveTargetId(null);
          }}
          onConfirm={() => {
            onChange(value.filter((row) => row.specialtyId !== removeTarget.specialtyId));
            setRemoveTargetId(null);
          }}
        />
      ) : null}
    </div>
  );
}
